import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import SalesTable from '../components/SalesTable';
import { salesService } from '../services';
import { formatCurrency } from '../utils/formatters';
import PageHeader from '../components/ui/PageHeader';
import Icon from '../components/nav/Icon';
import useConfirm from '../components/ui/useConfirm';

const Sales = () => {
    const [confirm, confirmDialog] = useConfirm();
    const [weeklySales, setWeeklySales] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [weekSales, setWeekSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [appMessage, setAppMessage] = useState({ text: '', type: '' });

    // Fetch all weekly sales
    const fetchWeeklySales = async () => {
        try {
            setLoading(true);
            const result = await salesService.getWeekly();
            setWeeklySales(result || []);
            return result || [];
        } catch (error) {
            // Only log errors to console, don't show to user
            console.error('Error fetching weekly sales:', error);
            // Set empty data instead of error
            setWeeklySales([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch sales for a specific week
    const fetchWeekSales = async (year, weekNumber) => {
        try {
            setLoading(true);
            const result = await salesService.getByWeek(year, weekNumber);
            setWeekSales(result.sales || []);
            setSelectedWeek(result.weekInfo);
        } catch (error) {
            // Only log errors to console, don't show to user
            console.error('Error fetching week sales:', error);
            // Set empty data instead of error
            setWeekSales([]);
            setSelectedWeek(null);
        } finally {
            setLoading(false);
        }
    };

    // Open the newest week straight away so the page is never empty.
    useEffect(() => {
        fetchWeeklySales().then((weeks) => {
            if (weeks && weeks.length > 0) fetchWeekSales(weeks[0].year, weeks[0].weekNumber);
        });
    }, []);

    const handleWeekSelect = (week) => {
        fetchWeekSales(week.year, week.weekNumber);
    };

    const handleRefresh = async () => {
        try {
            await fetchWeeklySales();
            if (selectedWeek) {
                await fetchWeekSales(selectedWeek.year, selectedWeek.weekNumber);
            }
            setAppMessage({ text: 'Sales data refreshed successfully!', type: 'success' });
            setTimeout(() => setAppMessage({ text: '', type: '' }), 3000);
        } catch (error) {
            console.error("Failed to refresh data:", error);
            setAppMessage({ text: 'Failed to refresh data.', type: 'error' });
            setTimeout(() => setAppMessage({ text: '', type: '' }), 3000);
        }
    };

    const handleDeleteSale = async (saleId) => {
        if (await confirm('This sale will be removed for good.', { title: 'Delete this sale?', confirmText: 'Delete' })) {
            try {
                await salesService.delete(saleId);
                // Refresh data after deletion
                if (selectedWeek) {
                    await fetchWeekSales(selectedWeek.year, selectedWeek.weekNumber);
                }
                await fetchWeeklySales();
                setAppMessage({ text: 'Sale record deleted.', type: 'success' });
                setTimeout(() => setAppMessage({ text: '', type: '' }), 3000);
            } catch (error) {
                console.error("Failed to delete sale:", error);
                setAppMessage({ text: 'Failed to delete sale.', type: 'error' });
                setTimeout(() => setAppMessage({ text: '', type: '' }), 3000);
            }
        }
    };

    const formatWeekRange = (startDate, endDate) => {
        const start = new Date(startDate).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
        });
        const end = new Date(endDate).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
        return `${start} - ${end}`;
    };

    const isSelected = (week) =>
        selectedWeek && selectedWeek.year === week.year && selectedWeek.weekNumber === week.weekNumber;

    return (
        <DashboardLayout>
            <div className="space-y-4 md:space-y-6">
                <PageHeader
                    title="Sales"
                    subtitle="All sales, grouped by week"
                    actions={
                        <button
                            type="button"
                            onClick={handleRefresh}
                            aria-label="Refresh"
                            className="h-10 w-10 md:w-auto md:px-4 inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                            <Icon name="refresh" className="w-5 h-5" />
                            <span className="hidden md:inline">Refresh</span>
                        </button>
                    }
                />

                {appMessage.text && (
                    <div role="status" className={`p-3 rounded-xl text-sm font-medium ${
                        appMessage.type === 'success' ? 'bg-emerald-50 text-emerald-800'
                            : appMessage.type === 'error' ? 'bg-red-50 text-red-800'
                            : 'bg-blue-50 text-blue-800'
                    }`}>
                        {appMessage.text}
                    </div>
                )}

                {/* Weeks: a swipeable row on phones, a grid on larger screens */}
                <section className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h2 className="text-base font-semibold text-slate-900">Weeks</h2>
                        <span className="text-xs text-slate-500">{weeklySales.length} weeks</span>
                    </div>

                    {loading && weeklySales.length === 0 ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : weeklySales.length === 0 ? (
                        <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-slate-200">
                            <p className="text-sm text-slate-500">No sales recorded yet.</p>
                        </div>
                    ) : (
                        <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3 overflow-x-auto hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0 snap-x">
                            {weeklySales.map((week) => (
                                <button
                                    type="button"
                                    key={week.weekKey}
                                    onClick={() => handleWeekSelect(week)}
                                    aria-pressed={Boolean(isSelected(week))}
                                    className={`min-w-[70%] md:min-w-0 snap-start text-left p-4 rounded-2xl border transition-colors ${
                                        isSelected(week) ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                                    }`}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <span className="text-sm font-semibold text-slate-900">Week {week.weekNumber}, {week.year}</span>
                                        <span className="text-xs text-slate-500 shrink-0">{week.totalSales} sales</span>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">{formatWeekRange(week.startDate, week.endDate)}</div>
                                    <div className="mt-2 text-lg font-semibold text-emerald-600 tabular-nums">{formatCurrency(week.totalAmount)}</div>
                                    <div className="text-xs text-slate-500 tabular-nums">
                                        Avg {formatCurrency(week.totalSales > 0 ? week.totalAmount / week.totalSales : 0)}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                {selectedWeek ? (
                    <section className="space-y-3">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
                            <div>
                                <h2 className="text-base font-semibold text-slate-900">
                                    Week {selectedWeek.weekNumber}, {selectedWeek.year}
                                </h2>
                                <p className="text-xs text-slate-500">{formatWeekRange(selectedWeek.startDate, selectedWeek.endDate)}</p>
                            </div>
                            <div className="flex gap-4 text-sm">
                                <span className="text-slate-500">Total <span className="font-semibold text-emerald-600 tabular-nums">{formatCurrency(selectedWeek.totalAmount)}</span></span>
                                <span className="text-slate-500">Sales <span className="font-semibold text-slate-900 tabular-nums">{selectedWeek.totalSales}</span></span>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : (
                            <SalesTable sales={weekSales} onDeleteSale={handleDeleteSale} />
                        )}
                    </section>
                ) : (
                    !loading && weeklySales.length > 0 && (
                        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-6 text-center">
                            <p className="text-sm text-slate-500">Tap a week to see its sales.</p>
                        </div>
                    )
                )}
            </div>
            {confirmDialog}
        </DashboardLayout>
    );
};

export default Sales;
